import { isEqual } from 'lodash-es';
import { localeOption, PrimeReactContext } from 'primereact/api';
import { Calendar as PrimeReactCalendar } from 'primereact/calendar';
import type { FormEvent } from 'primereact/ts-helpers';
import type { ComponentProps } from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../../common/functions/cn';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import { FieldValue } from '../../field/field';
import OdinIcon from '../../odin-icon/odin-icon.component';
import type { FieldValidation } from './calendar-functions';
import {
  DEFAULT_LOCALE,
  formatDisplayDate,
  normalizeDateFormat,
  parseDateTime,
  resolveDateBounds,
  resolveYearRange,
} from './calendar-functions';

export interface OdinCalendarProps
  extends Pick<
      DynamicFieldComponentProps<Date | Date[] | null | undefined>,
      'value' | 'onChange' | 'readOnly' | 'disabled' | 'placeholder'
    >,
    Pick<ComponentProps<'input'>, 'id' | 'aria-label' | 'aria-invalid' | 'aria-required' | 'aria-describedby'> {
  showIcon?: boolean;
  iconPos?: 'left' | 'right';
  readOnlyInput?: boolean;
  showOnFocus?: boolean;
  showButtonBar?: boolean;
  appendTo?: 'self' | HTMLElement | undefined | null;
  selectionMode?: 'single' | 'multiple' | 'range';
  fieldValidations?: FieldValidation;
  /** PrimeReact date format string (e.g. 'dd/mm/yy'). When set, overrides the locale default. */
  dateFormat?: string;
}

function OdinCalendar({
  id,
  value,
  onChange,
  readOnly,
  showIcon = true,
  iconPos = 'left',
  readOnlyInput = false,
  showOnFocus = true,
  showButtonBar = false,
  appendTo = 'self',
  selectionMode = 'single',
  disabled,
  placeholder,
  fieldValidations,
  dateFormat,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
  'aria-describedby': ariaDescribedBy,
}: OdinCalendarProps) {
  const [valueState, setValueState] = useState(value);
  const primeReactContext = useContext(PrimeReactContext);

  const calendarRef = useRef<PrimeReactCalendar>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only sync when value prop changes, check state to prevent redundant updates
  useEffect(() => {
    if (!isEqual(value, valueState)) {
      setValueState(value);
    }
  }, [value]);

  const normalizedDateFormat = normalizeDateFormat(dateFormat);

  const handleParseDateTime = useCallback(
    (text: string) => {
      return parseDateTime(text, primeReactContext?.locale ?? DEFAULT_LOCALE, dateFormat);
    },
    [dateFormat, primeReactContext?.locale]
  );

  const handleChange = (event: FormEvent) => {
    setValueState(event.value);
    onChange?.(event.value);
  };

  const { minDate, maxDate } = useMemo(() => resolveDateBounds(fieldValidations), [fieldValidations]);

  // PrimeReact clamps its view date to minDate/maxDate in place, and without a viewDate that is our value.
  const viewDate = useMemo(() => {
    const date = Array.isArray(valueState) ? valueState[0] : valueState;
    return date instanceof Date ? new Date(date) : undefined;
  }, [valueState]);

  const yearRange = useMemo(
    () => resolveYearRange({ minDate, maxDate, value: valueState }),
    [minDate, maxDate, valueState]
  );

  const handleShow = () => {
    calendarRef.current?.getInput()?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.code === 'Enter' || event.code === 'NumpadEnter') {
      // PrimeReact has no Enter handler on the input, so the browser fires a synthetic click on the
      // overlay's first focusable element (the "Choose Month" button), switching to month-picker view.
      // Blurring the input triggers PrimeReact's own blur/parse/onChange path — same as Tab.
      if (event.target === calendarRef.current?.getInput()) {
        event.preventDefault();
        calendarRef.current?.getInput()?.blur();
        calendarRef.current?.hide();
      }
    }
  };

  const decreaseYear = () => {
    const currentViewDate = calendarRef.current?.getViewDate() as Date;
    const newViewDate = new Date(currentViewDate);
    newViewDate.setFullYear(newViewDate.getFullYear() - 1);
    calendarRef.current?.updateViewDate(null, newViewDate);
  };

  const increaseYear = () => {
    const currentViewDate = calendarRef.current?.getViewDate() as Date;
    const newViewDate = new Date(currentViewDate);
    newViewDate.setFullYear(newViewDate.getFullYear() + 1);
    calendarRef.current?.updateViewDate(null, newViewDate);
  };

  const headerTemplate = () => (
    <>
      <button type="button" className="flex items-center justify-center p-datepicker-year-prev" onClick={decreaseYear}>
        <OdinIcon icon="ChevronsLeft" className="size-5" />
      </button>
      <button
        type="button"
        className="order-1 flex items-center justify-center p-datepicker-year-next"
        onClick={increaseYear}
      >
        <OdinIcon icon="ChevronsRight" className="size-5" />
      </button>
    </>
  );

  const icon = <OdinIcon icon="Calendar" className="size-4" />;
  const prevIcon = <OdinIcon icon="ChevronLeft" className="size-5" />;
  const nextIcon = <OdinIcon icon="ChevronRight" className="size-5" />;

  const isDisabled = !!disabled || !!readOnly;
  const isInvalid = ariaInvalid === true || ariaInvalid === 'true';
  const displayDateFormat = normalizedDateFormat ?? localeOption('dateFormat', primeReactContext?.locale ?? 'en') ?? '';

  if (readOnly && !Array.isArray(valueState)) {
    return (
      <FieldValue id={id} isEmpty={!valueState}>
        {valueState && formatDisplayDate(valueState, displayDateFormat)}
      </FieldValue>
    );
  }

  return (
    <PrimeReactCalendar
      ref={calendarRef}
      inputId={id}
      className={cn(
        // The root is a span, so it needs a display before height means anything, and flex-1
        // would let flex-basis govern that height. A utility here outranks calendar.css.
        'odin flex w-full items-center gap-2 px-2.5',
        'h-8',
        isInvalid && 'has-error',
        disabled && !readOnly && 'cursor-not-allowed bg-muted opacity-50',
        readOnly && 'border-none p-0'
      )}
      inputClassName="min-w-0 flex-1 self-stretch text-sm"
      panelClassName={cn('min-w-[320px]')}
      appendTo={appendTo}
      showOnFocus={showOnFocus}
      locale={primeReactContext?.locale}
      dateFormat={normalizedDateFormat}
      value={valueState}
      viewDate={viewDate}
      onChange={handleChange}
      ariaLabel={ariaLabel}
      pt={{
        root: { onKeyDown: handleKeyDown },
        input: {
          root: {
            'aria-invalid': ariaInvalid,
            'aria-required': ariaRequired,
            'aria-describedby': ariaDescribedBy,
          },
        },
      }}
      disabled={isDisabled}
      icon={icon}
      showIcon={showIcon && !readOnly}
      iconPos={iconPos}
      readOnlyInput={readOnlyInput}
      showButtonBar={showButtonBar}
      selectionMode={selectionMode}
      headerTemplate={headerTemplate}
      prevIcon={prevIcon}
      nextIcon={nextIcon}
      yearNavigator
      yearRange={yearRange}
      monthNavigator
      parseDateTime={handleParseDateTime}
      onShow={handleShow}
      placeholder={placeholder}
      minDate={minDate}
      maxDate={maxDate}
      showMinMaxRange={Boolean(minDate || maxDate)}
      selectOtherMonths
    />
  );
}

export default OdinCalendar;
